import { zodResolver } from '@hookform/resolvers/zod';
import {
  customersControllerCreateContact,
  customersControllerDeleteContact,
  customersControllerFindOne,
  customersControllerUpdate,
  customersControllerUpdateContact,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Briefcase,
  Building2,
  Calendar,
  Check,
  Clock,
  FileCheck,
  FileText,
  Hash,
  Info,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Plus,
  Save,
  Shield,
  Star,
  Trash2,
  Upload,
  User,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormField } from '../../../components/forms/form-field';
import { FormSection } from '../../../components/forms/form-section';
import { AppLayout } from '../../../components/layout/app-layout';
import { Breadcrumb } from '../../../components/layout/breadcrumb';
import { GradientAvatar } from '../../../components/shared/gradient-avatar';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { PhoneInput } from '../../../components/ui/phone-input';
import { usePermissions } from '../../../hooks/use-permissions';
import { api } from '../../../lib/ky';
import { formatUaePhone } from '../../../lib/phone';
import { cn } from '../../../lib/utils';
import { contactSchema, emailOptional, uaePhoneOptional } from '../../../lib/validations';
import { useAuthStore } from '../../../stores/auth.store';

// ── Zod schemas ───────────────────────────────────────────────────────────────

const orgSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters'),
  sapCode: z.string().optional(),
  accountType: z.string().optional(),
  primaryEmail: emailOptional,
  primaryPhone: uaePhoneOptional,
  tradeLicenseNo: z.string().optional(),
  vatRegistrationNo: z.string().optional(),
});
type OrgFormValues = z.infer<typeof orgSchema>;

type ContactFormValues = z.infer<typeof contactSchema>;

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/customers/$customerId/edit')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('customer:edit')) throw redirect({ to: '/dashboard' });
  },
  component: CustomerEditPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface Contact {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  designation?: string | null;
  isPrimary: boolean;
}

interface CustomerDetail {
  id: string;
  name: string;
  accountType: string;
  status: string;
  sapCode?: string | null;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  tradeLicenseNo?: string | null;
  vatRegistrationNo?: string | null;
  tradeLicenseKey?: string | null;
  vatRegistrationKey?: string | null;
  createdAt: string;
  contacts: Contact[];
}

// ── Page ──────────────────────────────────────────────────────────────────────

function CustomerEditPage() {
  const { customerId } = Route.useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const qc = useQueryClient();

  const { data: customer, isLoading } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () =>
      customersControllerFindOne({ id: customerId }) as unknown as Promise<CustomerDetail>,
  });

  // Org info form (seeded from API)
  const orgForm = useForm<OrgFormValues>({
    resolver: zodResolver(orgSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      sapCode: '',
      accountType: '',
      primaryEmail: '',
      primaryPhone: '',
      tradeLicenseNo: '',
      vatRegistrationNo: '',
    },
  });

  // Seed org form once customer loads
  useEffect(() => {
    if (!customer) return;
    orgForm.reset({
      name: customer.name,
      sapCode: customer.sapCode ?? '',
      accountType: customer.accountType ?? '',
      primaryEmail: customer.primaryEmail ?? '',
      primaryPhone: formatUaePhone(customer.primaryPhone ?? ''),
      tradeLicenseNo: customer.tradeLicenseNo ?? '',
      vatRegistrationNo: customer.vatRegistrationNo ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  // Contact add form
  const contactForm = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { fullName: '', email: '', phone: '', designation: '', isPrimary: false },
  });

  // Contact add/edit state
  const [addingContact, setAddingContact] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  // Document upload state
  const [tradeLicenseDragging, setTradeLicenseDragging] = useState(false);
  const [vatDragging, setVatDragging] = useState(false);

  // UI state
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  // Derive name/email for display
  const name = orgForm.watch('name');
  const primaryEmail = orgForm.watch('primaryEmail');
  const primaryPhone = orgForm.watch('primaryPhone');
  const sapCode = orgForm.watch('sapCode');
  const accountType = orgForm.watch('accountType');
  const tradeLicenseNo = orgForm.watch('tradeLicenseNo');
  const vatRegistrationNo = orgForm.watch('vatRegistrationNo');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['customer', customerId] });

  const hasChanges = useMemo(() => {
    if (!customer) return false;
    return (
      name !== customer.name ||
      sapCode !== (customer.sapCode ?? '') ||
      primaryEmail !== (customer.primaryEmail ?? '') ||
      primaryPhone !== (customer.primaryPhone ?? '') ||
      accountType !== (customer.accountType ?? '') ||
      tradeLicenseNo !== (customer.tradeLicenseNo ?? '') ||
      vatRegistrationNo !== (customer.vatRegistrationNo ?? '')
    );
  }, [
    name,
    sapCode,
    primaryEmail,
    primaryPhone,
    accountType,
    tradeLicenseNo,
    vatRegistrationNo,
    customer,
  ]);

  const updateMutation = useMutation({
    mutationFn: (data: OrgFormValues) =>
      customersControllerUpdate({
        id: customerId,
        requestBody: {
          name: data.name,
          sapCode: data.sapCode || undefined,
          primaryEmail: data.primaryEmail || undefined,
          primaryPhone: data.primaryPhone || undefined,
          accountType: data.accountType,
          tradeLicenseNo: data.tradeLicenseNo || undefined,
          vatRegistrationNo: data.vatRegistrationNo || undefined,
        },
      }),
    onSuccess: () => {
      invalidate();
      navigate({ to: '/customers/$customerId', params: { customerId } });
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const raw = body?.message;
      const lines = Array.isArray(raw) ? raw : [raw ?? 'Failed to save changes.'];
      toast.error('Failed to save', { description: lines.join(' · '), duration: 5000 });
      setSaving(false);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () =>
      customersControllerUpdate({ id: customerId, requestBody: { status: 'INACTIVE' } }),
    onSuccess: () => navigate({ to: '/customers' }),
  });

  const addContactMutation = useMutation({
    mutationFn: (data: ContactFormValues) =>
      customersControllerCreateContact({
        id: customerId,
        requestBody: {
          fullName: data.fullName.trim(),
          email: data.email?.trim() || '',
          phone: data.phone?.trim() || '',
          designation: data.designation?.trim() || undefined,
          isPrimary: data.isPrimary,
        },
      }),
    onSuccess: () => {
      invalidate();
      setAddingContact(false);
      contactForm.reset();
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const raw = body?.message;
      const lines = Array.isArray(raw) ? raw : [raw ?? 'Failed to add contact.'];
      toast.error('Failed to add contact', { description: lines.join(' · '), duration: 5000 });
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) =>
      customersControllerDeleteContact({ id: customerId, contactId }),
    onSuccess: () => {
      invalidate();
      setDeleteConfirm(null);
    },
  });

  // Edit contact form
  const editContactForm = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { fullName: '', email: '', phone: '', designation: '', isPrimary: false },
  });

  const updateContactMutation = useMutation({
    mutationFn: (data: ContactFormValues) =>
      customersControllerUpdateContact({
        id: customerId,
        contactId: editingContact!.id,
        requestBody: {
          fullName: data.fullName.trim(),
          email: data.email?.trim() || undefined,
          phone: data.phone?.trim() || undefined,
          designation: data.designation?.trim() || undefined,
          isPrimary: data.isPrimary,
        },
      }),
    onSuccess: () => {
      invalidate();
      setEditingContact(null);
      editContactForm.reset();
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const raw = body?.message;
      const lines = Array.isArray(raw) ? raw : [raw ?? 'Failed to update contact.'];
      toast.error('Failed to update contact', { description: lines.join(' · '), duration: 5000 });
    },
  });

  const uploadDocMutation = useMutation({
    mutationFn: async ({
      file,
      type,
    }: { file: File; type: 'TRADE_LICENSE' | 'VAT_REGISTRATION' }) => {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`customers/${customerId}/documents/${type}`, { body: fd, headers: {} });
    },
    onSuccess: () => invalidate(),
    onError: (err: unknown) => {
      toast.error('Upload failed', { description: String(err), duration: 5000 });
    },
  });

  const removeDocMutation = useMutation({
    mutationFn: (type: 'TRADE_LICENSE' | 'VAT_REGISTRATION') =>
      api.delete(`customers/${customerId}/documents/${type}`),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => {
      toast.error('Remove failed', { description: String(err), duration: 5000 });
    },
  });

  const handleDocFile = (file: File, type: 'TRADE_LICENSE' | 'VAT_REGISTRATION') => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10 MB limit.');
      return;
    }
    uploadDocMutation.mutate({ file, type });
  };

  const handleSave = orgForm.handleSubmit((data) => {
    setSaving(true);
    updateMutation.mutate(data);
  });

  const handleDiscard = () => {
    if (!customer) return;
    orgForm.reset({
      name: customer.name,
      sapCode: customer.sapCode ?? '',
      accountType: customer.accountType ?? '',
      primaryEmail: customer.primaryEmail ?? '',
      primaryPhone: formatUaePhone(customer.primaryPhone ?? ''),
      tradeLicenseNo: customer.tradeLicenseNo ?? '',
      vatRegistrationNo: customer.vatRegistrationNo ?? '',
    });
  };

  const displayInitials = name.trim()
    ? name
        .trim()
        .split(' ')
        .filter(Boolean)
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '??';

  const sinceYear = customer?.createdAt ? new Date(customer.createdAt).getFullYear() : null;
  const isActive = customer?.status === 'ACTIVE';

  if (isLoading || !customer) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout noPadding>
      <div className="flex flex-col min-h-screen">
        {/* ── Hero header ── */}
        <div className="bg-white border-b border-gray-200 px-6 sm:px-8 py-5">
          <Breadcrumb
            items={[
              { label: 'Customers', href: '/customers' },
              { label: customer.name, href: `/customers/${customerId}` },
              { label: 'Edit' },
            ]}
          />

          <div className="flex items-start justify-between gap-4 mt-3">
            <div className="flex items-start gap-4">
              <button
                type="button"
                onClick={() => navigate({ to: '/customers/$customerId', params: { customerId } })}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-700 shrink-0 mt-0.5"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center shadow-lg shrink-0">
                  <span className="text-white font-bold text-lg">{displayInitials}</span>
                </div>
                <div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                      {name || 'Edit Customer'}
                    </h1>
                    {hasChanges && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-warning-50 text-warning-600 border border-warning-300 uppercase tracking-wide">
                        Unsaved
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {customer.id.slice(0, 8).toUpperCase()}
                    </span>
                    {sinceYear && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Since {sinceYear}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Updated{' '}
                      {new Date(customer.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span
                        className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          isActive ? 'bg-success-500 animate-pulse' : 'bg-gray-400',
                        )}
                      />
                      <span className={isActive ? 'text-success-600 font-medium' : 'text-gray-500'}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2.5 shrink-0">
              {hasChanges && (
                <Button variant="outline" size="sm" onClick={handleDiscard}>
                  Discard
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate({ to: '/customers/$customerId', params: { customerId } })}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={saving || !hasChanges}
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex flex-1 min-h-0">
          {/* ── Left form ── */}
          <div className="flex-1 px-6 sm:px-8 py-7 space-y-5 overflow-y-auto min-w-0">
            {/* Organization Info */}
            <FormSection
              icon={<Building2 className="w-4 h-4" />}
              iconBg="bg-brand-50 text-brand-500"
              title="Organization Info"
              subtitle="Core identity and contact information"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormField
                  className="sm:col-span-2"
                  label="Organization Name"
                  required
                  error={orgForm.formState.errors.name?.message}
                >
                  <Input
                    {...orgForm.register('name')}
                    placeholder="e.g. Acme Construction Inc."
                    hasError={!!orgForm.formState.errors.name}
                  />
                </FormField>

                <FormField label="SAP Code" hint="From ERP system">
                  <Input {...orgForm.register('sapCode')} placeholder="e.g. SAP-00472831" />
                </FormField>

                <FormField label="Account Type">
                  <Input
                    {...orgForm.register('accountType')}
                    placeholder="e.g. General Contractor"
                  />
                </FormField>

                <FormField
                  label="Primary Email"
                  required
                  error={orgForm.formState.errors.primaryEmail?.message}
                >
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                      {...orgForm.register('primaryEmail')}
                      type="email"
                      placeholder="info@company.com"
                      hasError={!!orgForm.formState.errors.primaryEmail}
                      className="pl-10"
                    />
                  </div>
                </FormField>

                <FormField
                  label="Primary Phone"
                  error={orgForm.formState.errors.primaryPhone?.message}
                >
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <PhoneInput
                      {...orgForm.register('primaryPhone')}
                      placeholder="+971 50 123 4567"
                      hasError={!!orgForm.formState.errors.primaryPhone}
                      className="pl-10"
                    />
                  </div>
                </FormField>

                <FormField label="Trade License Number" hint="Identifier, not the document">
                  <Input {...orgForm.register('tradeLicenseNo')} placeholder="e.g. TRN262040224" />
                </FormField>

                <FormField label="VAT Registration Number" hint="Identifier, not the document">
                  <Input
                    {...orgForm.register('vatRegistrationNo')}
                    placeholder="e.g. 100234567800003"
                  />
                </FormField>
              </div>
            </FormSection>

            {/* Documents */}
            <FormSection
              icon={<Upload className="w-4 h-4" />}
              iconBg="bg-brand-50 text-brand-500"
              title="Documents"
              subtitle="Compliance and legal documents"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(
                  [
                    {
                      label: 'Trade License',
                      type: 'TRADE_LICENSE' as const,
                      hasDoc: !!customer.tradeLicenseKey,
                      icon: FileText,
                      gradient: 'from-emerald-500 to-teal-600',
                      dragging: tradeLicenseDragging,
                      setDragging: setTradeLicenseDragging,
                    },
                    {
                      label: 'VAT Certificate',
                      type: 'VAT_REGISTRATION' as const,
                      hasDoc: !!customer.vatRegistrationKey,
                      icon: FileCheck,
                      gradient: 'from-blue-500 to-indigo-600',
                      dragging: vatDragging,
                      setDragging: setVatDragging,
                    },
                  ] as const
                ).map((doc) => {
                  const DocIcon = doc.icon;
                  const inputId = `doc-upload-${doc.type}`;
                  if (doc.hasDoc) {
                    return (
                      <div
                        key={doc.type}
                        className="rounded-2xl border border-success-200 bg-success-50/50 p-4"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className={cn(
                              'w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center shrink-0 shadow-sm',
                              doc.gradient,
                            )}
                          >
                            <DocIcon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">{doc.label}</p>
                            <p className="text-xs text-success-600 mt-0.5 font-medium">Uploaded</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <label htmlFor={inputId} className="flex-1 cursor-pointer">
                            <input
                              id={inputId}
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="sr-only"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleDocFile(f, doc.type);
                                e.target.value = '';
                              }}
                            />
                            <span className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 transition-colors cursor-pointer">
                              <Upload className="w-3 h-3" /> Replace
                            </span>
                          </label>
                          <button
                            type="button"
                            onClick={() => removeDocMutation.mutate(doc.type)}
                            disabled={removeDocMutation.isPending}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-error-200 bg-error-50 hover:bg-error-100 text-error-700 transition-colors disabled:opacity-50"
                          >
                            <X className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={doc.type}>
                      <label htmlFor={inputId} className="block cursor-pointer group">
                        <input
                          id={inputId}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="sr-only"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleDocFile(f, doc.type);
                            e.target.value = '';
                          }}
                        />
                        <div
                          className={cn(
                            'rounded-2xl border-2 border-dashed bg-white transition-all duration-150 p-6 flex flex-col items-center gap-2 text-center',
                            doc.dragging
                              ? 'border-brand-400 bg-brand-50/50 scale-[1.01]'
                              : 'border-gray-200 group-hover:border-brand-300 group-hover:bg-brand-50/30',
                          )}
                          onDragOver={(e) => {
                            e.preventDefault();
                            doc.setDragging(true);
                          }}
                          onDragLeave={() => doc.setDragging(false)}
                          onDrop={(e) => {
                            e.preventDefault();
                            doc.setDragging(false);
                            const f = e.dataTransfer.files?.[0];
                            if (f) handleDocFile(f, doc.type);
                          }}
                        >
                          <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-brand-50 flex items-center justify-center transition-colors">
                            <Upload className="w-5 h-5 text-gray-400 group-hover:text-brand-500 transition-colors" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-700">{doc.label}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              PDF, JPG or PNG · Max 10 MB
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-brand-500 group-hover:text-brand-600">
                            Click or drag to upload
                          </span>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </FormSection>

            {/* Contacts */}
            <FormSection
              icon={<Users className="w-4 h-4" />}
              iconBg="bg-warning-50 text-warning-600"
              title="Contacts"
              subtitle={`${customer.contacts.length} contact${customer.contacts.length !== 1 ? 's' : ''} on this account`}
            >
              <div className="space-y-3">
                {customer.contacts.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center gap-4 p-4 rounded-2xl border border-gray-200 bg-gray-50/50 group hover:border-gray-300 transition-all"
                  >
                    <GradientAvatar name={c.fullName} size="md" shape="rounded" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900">{c.fullName}</span>
                        {c.isPrimary && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-600 border border-brand-100">
                            <Star className="w-2.5 h-2.5" /> Primary
                          </span>
                        )}
                      </div>
                      {c.designation && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                          <Briefcase className="w-3 h-3" />
                          {c.designation}
                        </div>
                      )}
                    </div>
                    <div className="hidden sm:flex items-center gap-5 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        {c.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        {formatUaePhone(c.phone)}
                      </span>
                    </div>
                    {can('customer:create') && (
                      <div className="shrink-0 flex items-center gap-1">
                        {/* Edit button */}
                        <button
                          type="button"
                          onClick={() => {
                            setEditingContact(c);
                            editContactForm.reset({
                              fullName: c.fullName,
                              email: c.email ?? '',
                              phone: formatUaePhone(c.phone ?? ''),
                              designation: c.designation ?? '',
                              isPrimary: c.isPrimary,
                            });
                          }}
                          className="opacity-0 group-hover:opacity-100 w-8 h-8 rounded-xl hover:bg-brand-50 flex items-center justify-center text-gray-400 hover:text-brand-600 transition-all"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {/* Delete button */}
                        {customer.contacts.length === 1 ? null : c.isPrimary ? (
                          deleteConfirm === c.id ? (
                            <div className="flex items-center gap-1.5 text-xs">
                              <span className="text-warning-600 font-medium">
                                Assign a new primary first
                              </span>
                              <button
                                type="button"
                                onClick={() => setDeleteConfirm(null)}
                                className="text-gray-400 px-1.5 py-0.5 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                OK
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setDeleteConfirm(c.id)}
                              className="opacity-0 group-hover:opacity-100 w-8 h-8 rounded-xl hover:bg-error-50 flex items-center justify-center text-gray-400 hover:text-error-600 transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )
                        ) : deleteConfirm === c.id ? (
                          <div className="flex items-center gap-1.5 text-xs">
                            <span className="text-gray-500">Delete?</span>
                            <button
                              type="button"
                              onClick={() => deleteContactMutation.mutate(c.id)}
                              className="text-error-600 font-semibold hover:text-error-700 px-1.5 py-0.5 rounded-lg hover:bg-error-50 transition-colors"
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirm(null)}
                              className="text-gray-400 px-1.5 py-0.5 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(c.id)}
                            className="opacity-0 group-hover:opacity-100 w-8 h-8 rounded-xl hover:bg-error-50 flex items-center justify-center text-gray-400 hover:text-error-600 transition-all"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Edit contact modal (inline, shown when editingContact is set) */}
                {editingContact && (
                  <div className="rounded-2xl border border-dashed border-brand-300 bg-brand-50/20 p-5 space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center">
                        <Pencil className="w-3.5 h-3.5 text-brand-600" />
                      </div>
                      <span className="text-sm font-semibold text-brand-700">Edit Contact</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <FormField
                        label="Full Name"
                        required
                        error={editContactForm.formState.errors.fullName?.message}
                      >
                        <div className="relative">
                          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <Input
                            {...editContactForm.register('fullName')}
                            type="text"
                            placeholder="First and last name"
                            className="pl-10"
                            hasError={!!editContactForm.formState.errors.fullName}
                          />
                        </div>
                      </FormField>
                      <FormField label="Role / Title">
                        <div className="relative">
                          <Briefcase className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <Input
                            {...editContactForm.register('designation')}
                            type="text"
                            placeholder="e.g. Project Manager"
                            className="pl-10"
                          />
                        </div>
                      </FormField>
                      <FormField
                        label="Email"
                        error={editContactForm.formState.errors.email?.message}
                      >
                        <div className="relative">
                          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <Input
                            {...editContactForm.register('email')}
                            type="email"
                            placeholder="contact@company.com"
                            className="pl-10"
                            hasError={!!editContactForm.formState.errors.email}
                          />
                        </div>
                      </FormField>
                      <FormField
                        label="Phone"
                        error={editContactForm.formState.errors.phone?.message}
                      >
                        <div className="relative">
                          <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <PhoneInput
                            {...editContactForm.register('phone')}
                            placeholder="+971 50 123 4567"
                            className="pl-10"
                            hasError={!!editContactForm.formState.errors.phone}
                          />
                        </div>
                      </FormField>
                    </div>
                    {!editingContact.isPrimary && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          {...editContactForm.register('isPrimary')}
                          className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                        />
                        <span className="text-xs font-medium text-gray-700">
                          Make primary contact
                        </span>
                      </label>
                    )}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={editContactForm.handleSubmit((data) =>
                          updateContactMutation.mutate(data),
                        )}
                        disabled={updateContactMutation.isPending}
                      >
                        {updateContactMutation.isPending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Check className="w-3.5 h-3.5" />
                        )}
                        Save Changes
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingContact(null);
                          editContactForm.reset();
                        }}
                      >
                        <X className="w-3.5 h-3.5" /> Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* New contact form */}
                {addingContact && (
                  <div className="rounded-2xl border border-dashed border-brand-300 bg-brand-50/20 p-5 space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-brand-600" />
                      </div>
                      <span className="text-sm font-semibold text-brand-700">New Contact</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <FormField
                        label="Full Name"
                        required
                        error={contactForm.formState.errors.fullName?.message}
                      >
                        <div className="relative">
                          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <Input
                            {...contactForm.register('fullName')}
                            type="text"
                            placeholder="First and last name"
                            className="pl-10"
                            hasError={!!contactForm.formState.errors.fullName}
                          />
                        </div>
                      </FormField>
                      <FormField label="Role / Title">
                        <div className="relative">
                          <Briefcase className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <Input
                            {...contactForm.register('designation')}
                            type="text"
                            placeholder="e.g. Project Manager"
                            className="pl-10"
                          />
                        </div>
                      </FormField>
                      <FormField label="Email" error={contactForm.formState.errors.email?.message}>
                        <div className="relative">
                          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <Input
                            {...contactForm.register('email')}
                            type="email"
                            placeholder="contact@company.com"
                            className="pl-10"
                            hasError={!!contactForm.formState.errors.email}
                          />
                        </div>
                      </FormField>
                      <FormField label="Phone" error={contactForm.formState.errors.phone?.message}>
                        <div className="relative">
                          <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <PhoneInput
                            {...contactForm.register('phone')}
                            placeholder="+971 50 123 4567"
                            className="pl-10"
                            hasError={!!contactForm.formState.errors.phone}
                          />
                        </div>
                      </FormField>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={contactForm.handleSubmit((data) =>
                          addContactMutation.mutate(data),
                        )}
                        disabled={addContactMutation.isPending}
                      >
                        {addContactMutation.isPending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Check className="w-3.5 h-3.5" />
                        )}
                        Add Contact
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setAddingContact(false);
                          contactForm.reset();
                        }}
                      >
                        <X className="w-3.5 h-3.5" /> Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {!addingContact && !editingContact && can('customer:create') && (
                  <button
                    type="button"
                    onClick={() => setAddingContact(true)}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 border-dashed border-gray-200 hover:border-brand-300 hover:bg-brand-50/30 text-sm font-semibold text-gray-400 hover:text-brand-600 transition-all group"
                  >
                    <div className="w-5 h-5 rounded-full bg-gray-200 group-hover:bg-brand-100 flex items-center justify-center transition-colors">
                      <Plus className="w-3 h-3 group-hover:text-brand-600 transition-colors" />
                    </div>
                    Add Contact
                  </button>
                )}
              </div>
            </FormSection>

            {/* Danger Zone */}
            <div className="bg-white rounded-2xl border border-error-200/60 overflow-hidden shadow-theme-sm">
              <div className="px-6 py-5 border-b border-error-100 flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-error-50 text-error-500 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Danger Zone</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    These actions affect the entire account and cannot be undone.
                  </p>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50/50">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Deactivate account</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Suspend permit submissions and hide from active lists. All existing permits
                      are retained.
                    </p>
                  </div>
                  {confirmDeactivate ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-warning-600 font-medium">Confirm?</span>
                      <button
                        type="button"
                        onClick={() => deactivateMutation.mutate()}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-warning-500 text-white hover:bg-warning-600 transition-colors"
                      >
                        Deactivate
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeactivate(false)}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeactivate(true)}
                      className="px-4 py-2 text-sm font-semibold rounded-xl border border-warning-300 text-warning-600 bg-warning-50 hover:bg-warning-100 transition-all shrink-0 flex items-center gap-2"
                    >
                      <Ban className="w-3.5 h-3.5" /> Deactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Right sidebar ── */}
          <div className="hidden lg:block w-72 shrink-0 px-6 py-7">
            <div className="sticky top-20 space-y-4">
              {/* Live preview */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-theme-sm overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-lg bg-brand-50 flex items-center justify-center">
                      <Zap className="w-3.5 h-3.5 text-brand-500" />
                    </div>
                    <p className="font-semibold text-sm text-gray-900">Account Preview</p>
                  </div>
                  <p className="text-xs text-gray-400">Updates as you edit</p>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center shadow-md shrink-0">
                      <span className="text-white font-bold text-sm">{displayInitials}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {name || 'Organization Name'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        {primaryEmail || 'Email not set'}
                      </p>
                      {accountType && (
                        <p className="text-xs text-gray-400/70 truncate">{accountType}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Contacts', value: customer.contacts.length },
                      {
                        label: 'Documents',
                        value:
                          (customer.tradeLicenseKey ? 1 : 0) +
                          (customer.vatRegistrationKey ? 1 : 0),
                      },
                    ].map((s, i) => (
                      <div
                        key={i}
                        className="bg-gray-50 rounded-xl p-2.5 text-center border border-gray-100"
                      >
                        <p className="text-base font-bold text-gray-900">{s.value}</p>
                        <p className="text-[10px] text-gray-400">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-1.5 pt-2 border-t border-gray-100">
                    {[
                      { label: 'Organization name', done: !!name },
                      {
                        label: 'Email address',
                        done: !!primaryEmail && !orgForm.formState.errors.primaryEmail,
                      },
                      { label: 'Phone number', done: !!primaryPhone },
                      { label: 'SAP code', done: !!sapCode },
                      {
                        label: 'Primary contact',
                        done: customer.contacts.some((c) => c.isPrimary),
                      },
                      {
                        label: 'Document on file',
                        done: !!(customer.tradeLicenseKey || customer.vatRegistrationKey),
                      },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2.5 py-0.5">
                        <div
                          className={cn(
                            'w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-all',
                            item.done ? 'bg-success-500' : 'bg-gray-200',
                          )}
                        >
                          {item.done && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <span
                          className={cn(
                            'text-xs',
                            item.done ? 'text-gray-900 font-medium' : 'text-gray-400',
                          )}
                        >
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Account metadata */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-theme-sm">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                  Account Info
                </p>
                <div className="space-y-2.5 text-xs">
                  {[
                    {
                      icon: Hash,
                      label: 'Account ID',
                      value: customer.id.slice(0, 8).toUpperCase(),
                    },
                    {
                      icon: Calendar,
                      label: 'Customer since',
                      value: sinceYear ? String(sinceYear) : '—',
                    },
                    {
                      icon: Clock,
                      label: 'Last updated',
                      value: new Date(customer.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      }),
                    },
                    { icon: Shield, label: 'Status', value: isActive ? 'Active' : 'Inactive' },
                  ].map((row, i) => {
                    const Icon = row.icon;
                    return (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <Icon className="w-3 h-3 shrink-0" />
                          {row.label}
                        </div>
                        <span className="font-medium text-gray-900">{row.value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Hint */}
              <div className="bg-brand-50/60 border border-brand-100 rounded-2xl p-4">
                <div className="flex gap-2.5">
                  <Info className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Changes are saved immediately. Contacts and documents can be updated at any time
                    without affecting active permits.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
